import { useRef } from 'react';
import { useLocation } from 'react-router';
import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/Text';
import {
  SHELL_HEADER_SUBTITLE_CLASSNAME,
  SHELL_HEADER_TITLE_CLASSNAME,
} from '@cartwhl/ui/layout/StudioShellHeader';
import UserMenuWithCredits from '@cartwhl/ui/organisms/UserMenuWithCredits';

import { userStore } from 'stores/global';

interface CanvasLayoutHeaderProps {
  title: string;
  subtitle?: string;
  hasHeaderContent?: boolean;
  onStartHeaderContentContainerChange?: (node: HTMLDivElement | null) => void;
  onCenterHeaderContentContainerChange?: (node: HTMLDivElement | null) => void;
  onEndHeaderContentContainerChange?: (node: HTMLDivElement | null) => void;
}

export default function CanvasLayoutHeader({
  title,
  subtitle,
  hasHeaderContent = false,
  onStartHeaderContentContainerChange,
  onCenterHeaderContentContainerChange,
  onEndHeaderContentContainerChange,
}: CanvasLayoutHeaderProps) {
  const { pathname } = useLocation();
  const { loading: userLoading } = useSnapshot(userStore.state);
  const endGroupRef = useRef<HTMLDivElement>(null);

  return (
    <header className="bg-primary relative flex h-16 shrink-0 items-center justify-between gap-4 px-4 lg:px-6">
      <div className="relative z-20 flex min-w-0 items-center gap-[22px]">
        <div
          ref={onStartHeaderContentContainerChange}
          className="flex shrink-0 items-center empty:hidden"
        />
        <Text
          as="h1"
          variant="studio/title/lg"
          className={SHELL_HEADER_TITLE_CLASSNAME}
        >
          {title}
          {subtitle && !hasHeaderContent ? (
            <span className={SHELL_HEADER_SUBTITLE_CLASSNAME}>{subtitle}</span>
          ) : null}
        </Text>
      </div>
      <div className="pointer-events-none absolute inset-y-0 left-0 right-0 z-10 flex items-center justify-center overflow-hidden">
        <div
          ref={onCenterHeaderContentContainerChange}
          className="pointer-events-auto flex min-w-0 max-w-full items-center justify-center overflow-hidden px-4"
        />
      </div>
      <div ref={endGroupRef} className="relative z-20 flex min-w-0 items-center">
        {!userLoading ? (
          <UserMenuWithCredits
            currentPathname={pathname}
            currentSurface="apps"
            middleContent={
              <div
                ref={onEndHeaderContentContainerChange}
                className="flex min-w-0 items-center justify-end overflow-hidden empty:hidden"
              />
            }
          />
        ) : null}
      </div>
    </header>
  );
}
